import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { MovementV2PageCombinedState, MovementV2PagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { MovementV2PageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<MovementV2PageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: MovementV2PagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function MovementV2PageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<MovementV2PageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{ movement_id: pageData.data.movement.id }}>{children}</TrackingContext>
		</MovementV2PageDataContextProvider>
	);
}
